import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CookieConsent {
  necessary: boolean; // Always true, as necessary cookies don't require consent
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly COOKIE_CONSENT_KEY = 'mzm-cookie-consent';
  private readonly COOKIE_CONSENT_SHOWN_KEY = 'mzm-cookie-consent-shown';
  
  private consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  private bannerShownSubject = new BehaviorSubject<boolean>(false);
  
  consent$: Observable<CookieConsent | null> = this.consentSubject.asObservable();
  bannerShown$: Observable<boolean> = this.bannerShownSubject.asObservable();
  
  constructor() {
    this.initializeConsent();
  }
  
  private initializeConsent(): void {
    if (typeof window === 'undefined') {
      return; // Server-side rendering check
    }
    
    // Check if consent has been given before
    const storedConsent = localStorage.getItem(this.COOKIE_CONSENT_KEY);
    const bannerShown = localStorage.getItem(this.COOKIE_CONSENT_SHOWN_KEY);
    
    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(storedConsent) as CookieConsent;
        this.consentSubject.next(parsedConsent);
      } catch (e) {
        console.error('Error parsing stored cookie consent', e);
        this.consentSubject.next(null);
      }
    } else {
      this.consentSubject.next(null);
    }
    
    this.bannerShownSubject.next(bannerShown === 'true');
  }
  
  /**
   * Save user's cookie consent preferences
   */
  saveConsent(consent: CookieConsent): void {
    if (typeof window === 'undefined') {
      return;
    }
    
    localStorage.setItem(this.COOKIE_CONSENT_KEY, JSON.stringify(consent));
    localStorage.setItem(this.COOKIE_CONSENT_SHOWN_KEY, 'true');
    
    this.consentSubject.next(consent);
    this.bannerShownSubject.next(true);
    
    // Apply consent settings (enable/disable cookies based on preferences)
    this.applyConsentSettings(consent);
  }
  
  /**
   * Accept all cookies
   */
  acceptAll(): void {
    const fullConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    this.saveConsent(fullConsent);
  }
  
  /**
   * Accept only necessary cookies
   */
  acceptNecessaryOnly(): void {
    const necessaryOnlyConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    this.saveConsent(necessaryOnlyConsent);
  }
  
  /**
   * Save custom cookie preferences
   */
  savePreferences(analytics: boolean, marketing: boolean, preferences: boolean): void {
    const customConsent: CookieConsent = {
      necessary: true,
      analytics,
      marketing,
      preferences
    };
    
    this.saveConsent(customConsent);
  }
  
  /**
   * Check if consent has been given for a specific cookie type
   */
  hasConsent(type: keyof CookieConsent): boolean {
    const consent = this.consentSubject.value;
    
    if (!consent) {
      return type === 'necessary'; // Only necessary cookies are allowed without consent
    }
    
    return consent[type];
  }
  
  /**
   * Check if the consent banner has been shown
   */
  hasShownBanner(): boolean {
    return this.bannerShownSubject.value;
  }
  
  /**
   * Reset cookie consent (for testing or when policies change significantly)
   */
  resetConsent(): void {
    if (typeof window === 'undefined') {
      return;
    }
    
    localStorage.removeItem(this.COOKIE_CONSENT_KEY);
    localStorage.removeItem(this.COOKIE_CONSENT_SHOWN_KEY);
    
    this.consentSubject.next(null);
    this.bannerShownSubject.next(false);
  }
  
  /**
   * Apply consent settings by enabling/disabling cookies
   */
  private applyConsentSettings(consent: CookieConsent): void {
    // Set a cookie to store consent information (accessible server-side)
    this.setCookie('cookie_consent', JSON.stringify(consent), 365);
    
    // Apply settings to various services
    if (consent.analytics) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }
    
    if (consent.marketing) {
      this.enableMarketing();
    } else {
      this.disableMarketing();
    }
    
    if (consent.preferences) {
      this.enablePreferences();
    } else {
      this.disablePreferences();
    }
    
    // Dispatch a custom event that other scripts can listen for
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('cookieConsentUpdated', { detail: consent });
      window.dispatchEvent(event);
    }
  }
  
  /**
   * Set a cookie with the given name, value and expiration days
   */
  private setCookie(name: string, value: string, days: number): void {
    if (typeof document === 'undefined') {
      return;
    }
    
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = '; expires=' + date.toUTCString();
    
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }
  
  /**
   * Delete a cookie by name
   */
  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';
  }
  
  /**
   * Enable analytics tracking
   */
  private enableAnalytics(): void {
    // Google Analytics (GA4)
    if (typeof window !== 'undefined') {
      // For Google Analytics 4
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
      
      // For older Universal Analytics
      if ((window as any).ga) {
        (window as any).ga('set', 'anonymizeIp', false);
      }
      
      // For Google Tag Manager
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': 'analytics_consent_granted'
        });
      }
    }
    
    // Set a cookie to remember this setting
    this.setCookie('analytics_enabled', 'true', 365);
  }
  
  /**
   * Disable analytics tracking
   */
  private disableAnalytics(): void {
    // Google Analytics (GA4)
    if (typeof window !== 'undefined') {
      // For Google Analytics 4
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
      
      // For older Universal Analytics
      if ((window as any).ga) {
        (window as any).ga('set', 'anonymizeIp', true);
      }
      
      // For Google Tag Manager
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': 'analytics_consent_denied'
        });
      }
    }
    
    // Remove any analytics cookies
    this.deleteCookie('_ga');
    this.deleteCookie('_gid');
    this.deleteCookie('_gat');
    this.deleteCookie('_ga_' + window.location.hostname.replace(/\./g, '-'));
    
    // Set a cookie to remember this setting
    this.setCookie('analytics_enabled', 'false', 365);
  }
  
  /**
   * Enable marketing cookies
   */
  private enableMarketing(): void {
    if (typeof window !== 'undefined') {
      // For Google Ads
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted'
        });
      }
      
      // For Facebook Pixel
      if ((window as any).fbq) {
        (window as any).fbq('consent', 'grant');
      }
      
      // For Google Tag Manager
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': 'marketing_consent_granted'
        });
      }
    }
    
    // Set a cookie to remember this setting
    this.setCookie('marketing_enabled', 'true', 365);
  }
  
  /**
   * Disable marketing cookies
   */
  private disableMarketing(): void {
    if (typeof window !== 'undefined') {
      // For Google Ads
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }
      
      // For Facebook Pixel
      if ((window as any).fbq) {
        (window as any).fbq('consent', 'revoke');
      }
      
      // For Google Tag Manager
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          'event': 'marketing_consent_denied'
        });
      }
    }
    
    // Remove common marketing cookies
    this.deleteCookie('_fbp');
    this.deleteCookie('_gcl_au');
    this.deleteCookie('IDE');
    this.deleteCookie('DSID');
    
    // Set a cookie to remember this setting
    this.setCookie('marketing_enabled', 'false', 365);
  }
  
  /**
   * Enable preferences cookies
   */
  private enablePreferences(): void {
    // Set a cookie to remember this setting
    this.setCookie('preferences_enabled', 'true', 365);
    
    // For Google Tag Manager
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'preferences_consent_granted'
      });
    }
  }
  
  /**
   * Disable preferences cookies
   */
  private disablePreferences(): void {
    // Remove any preference cookies (site-specific)
    // Example: this.deleteCookie('theme_preference');
    
    // Set a cookie to remember this setting
    this.setCookie('preferences_enabled', 'false', 365);
    
    // For Google Tag Manager
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'preferences_consent_denied'
      });
    }
  }
}
